/**
 * Reading a file's column names in the browser, and nowhere else.
 *
 * The wizard offers the target column from the actual names in the file rather
 * than asking somebody to type one, and it does that by reading the first
 * 64 KB of the file **locally**, with the File API. Those bytes never leave
 * the machine: they are not sent to the console, they are not sent to storage,
 * and nothing here is a measurement — the file's real schema is measured by
 * the server when it reads the bytes it stored, and that is the only schema
 * any screen ever shows.
 *
 * A Parquet file has its schema in a footer this cannot reach without parsing
 * the format, so it falls back to typing the name. **A gzip falls back the same
 * way**: the server reads through one, this does not, and decoding compressed
 * bytes as text would offer a line of mojibake as column names — a claim about
 * a file nothing here read. Either way the target column is a *declaration*,
 * checked against the file at verify, and a declaration that turns out to be
 * wrong never rejects anything.
 */

import { HEADER_SAMPLE_BYTES } from "../config";
import type { FileFormat } from "../api/types";

/** The separators a hand-made export actually uses, in the order tried. */
const DELIMITERS = [",", ";", "\t", "|"] as const;

/**
 * Guess which separator a header line uses.
 *
 * Args:
 *     line: The first line of the file.
 *
 * Returns:
 *     The separator with the most occurrences, comma when there are none.
 */
function guessDelimiter(line: string): string {
  let best = ",";
  let bestCount = 0;
  for (const candidate of DELIMITERS) {
    const count = line.split(candidate).length - 1;
    if (count > bestCount) {
      best = candidate;
      bestCount = count;
    }
  }
  return best;
}

/**
 * Split a header line into column names, honouring simple quoting.
 *
 * Args:
 *     line: The first line of the file.
 *     delimiter: The separator to split on.
 *
 * Returns:
 *     The names, trimmed and unquoted, with the empty ones dropped.
 */
function splitHeader(line: string, delimiter: string): string[] {
  const names: string[] = [];
  let current = "";
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const character = line[index]!;
    if (character === '"') {
      if (quoted && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
      continue;
    }
    if (character === delimiter && !quoted) {
      names.push(current);
      current = "";
      continue;
    }
    current += character;
  }
  names.push(current);
  return names.map((name) => name.trim()).filter((name) => name.length > 0);
}

/**
 * Read the column names out of the first line of a delimited file.
 *
 * Args:
 *     file: The file the person picked. Only its first bytes are read.
 *     limit: How many bytes to read; the console's own sample size.
 *
 * Returns:
 *     The column names, or an empty list when the first line does not look
 *     like a header, or when the bytes are compressed. An empty list is not a
 *     refusal: the wizard falls back to typing the name, exactly as it does for
 *     a Parquet file.
 */
export async function readCsvColumns(
  file: File,
  limit: number = HEADER_SAMPLE_BYTES,
): Promise<string[]> {
  let text: string;
  try {
    const head = new Uint8Array(await file.slice(0, limit).arrayBuffer());
    // The magic number, not the name: `churn.csv.gz` is a supported CSV and a
    // gzip called `churn.csv` is a supported CSV too. Either way these are
    // compressed bytes, and reading them as text produces names nothing read.
    if (head[0] === 0x1f && head[1] === 0x8b) {
      return [];
    }
    text = new TextDecoder().decode(head);
  } catch {
    return [];
  }
  const firstBreak = text.search(/\r?\n/);
  const line = firstBreak === -1 ? text : text.slice(0, firstBreak);
  if (!line.trim()) {
    return [];
  }
  const names = splitHeader(line, guessDelimiter(line));
  // One column is not a header anybody needs a picker for, and a "header" with
  // a duplicate name is a file whose first line is data. Both fall back to
  // typing, which is always allowed.
  if (names.length < 2 || new Set(names).size !== names.length) {
    return [];
  }
  return names;
}

/**
 * Work out which of the two file kinds this is, from its name.
 *
 * This is a **declaration** and the wizard says so: the server checks the
 * file's first bytes against it when it reads the file, and a disagreement is
 * a rejection with a sentence naming both. Nothing here decides anything.
 *
 * Args:
 *     filename: The name of the file the person picked.
 *
 * Returns:
 *     The format to declare, or null when the extension is neither.
 */
export function formatFromFilename(filename: string): FileFormat | null {
  const lower = filename.toLowerCase().replace(/\.gz$/, "");
  if (lower.endsWith(".csv") || lower.endsWith(".tsv")) {
    return "csv";
  }
  if (lower.endsWith(".parquet") || lower.endsWith(".pq")) {
    return "parquet";
  }
  return null;
}

/**
 * Suggest a dataset name from a filename.
 *
 * A suggestion only: the person sees it in the field and may write anything.
 *
 * Args:
 *     filename: The name of the file the person picked.
 *
 * Returns:
 *     e.g. `churn 2026` from `churn-2026.parquet`.
 */
export function nameFromFilename(filename: string): string {
  return filename
    .replace(/\.(gz)$/i, "")
    .replace(/\.(csv|tsv|parquet|pq)$/i, "")
    .replace(/[_-]+/g, " ")
    .trim();
}
